import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, Clock, Loader } from "lucide-react";
import client from "../../api/client";

function formatCurrency(amount, currency) {
  if (!amount) return null;
  if (currency === "NGN") return `₦${Number(amount / 100).toLocaleString()}`;
  return `$${(amount / 100).toFixed(2)} USD`;
}

export default function ScholarshipPaymentStatus() {
  const [params] = useSearchParams();
  const status = params.get("payment_status");
  const ref = params.get("ref");

  const [application, setApplication] = useState(null);
  const [fetching, setFetching] = useState(!!ref);

  useEffect(() => {
    if (!ref) return;
    client
      .get(`/scholarships/application/${ref}`)
      .then((res) => setApplication(res.data?.data))
      .catch(() => {})
      .finally(() => setFetching(false));
  }, [ref]);

  const isSuccess    = status === "success";
  const isProcessing = status === "processing";
  const isFailed     = status === "failed";

  return (
    <>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #f8fafc; }
      `}</style>

      <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 16px" }}>
        <div style={{ width: "100%", maxWidth: 460 }}>
          <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #e2e8f0", boxShadow: "0 8px 40px rgba(0,0,0,0.06)", overflow: "hidden" }}>

            {/* Colour bar at top */}
            <div style={{
              height: 6,
              background: isSuccess ? "#10b981" : isProcessing ? "#f59e0b" : "#ef4444",
            }} />

            <div style={{ padding: "40px 36px" }}>

              {/* Icon */}
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
                {isSuccess && <CheckCircle size={56} color="#10b981" strokeWidth={1.5} />}
                {isProcessing && <Clock size={56} color="#f59e0b" strokeWidth={1.5} />}
                {isFailed && <XCircle size={56} color="#ef4444" strokeWidth={1.5} />}
              </div>

              {/* Title */}
              <h1 style={{ textAlign: "center", fontSize: 22, fontWeight: 800, color: "#0f172a", fontFamily: "Montserrat, sans-serif", marginBottom: 10 }}>
                {isSuccess    && "Application Received!"}
                {isProcessing && "Payment Processing…"}
                {isFailed     && "Payment Failed"}
              </h1>

              {/* Subtitle */}
              <p style={{ textAlign: "center", fontSize: 14, color: "#64748b", lineHeight: 1.7, marginBottom: 32 }}>
                {isSuccess    && "Your payment was confirmed. Check your email for next steps."}
                {isProcessing && "We've received your payment and are confirming it now. You'll get an email shortly."}
                {isFailed     && "Your payment could not be completed. You can try again below."}
              </p>

              {/* Application details (success only) */}
              {isSuccess && (
                fetching ? (
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 32 }}>
                    <Loader size={20} color="#94a3b8" style={{ animation: "spin 0.8s linear infinite" }} />
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  </div>
                ) : application ? (
                  <div style={{ background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0", padding: "20px 24px", marginBottom: 32 }}>
                    {[
                      { label: "Name",        value: application.name },
                      { label: "Scholarship", value: application.scholarship },
                      { label: "Amount Paid", value: formatCurrency(application.amount, application.currency) },
                      { label: "Onboarding",  value: application.onboarding_date },
                    ].filter(r => r.value).map(({ label, value }) => (
                      <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: "1px solid #f1f5f9" }}>
                        <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
                        <span style={{ fontSize: 13, color: "#0f172a", fontWeight: 700, textAlign: "right", maxWidth: "60%" }}>{value}</span>
                      </div>
                    ))}
                  </div>
                ) : null
              )}

              {/* CTA */}
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {isFailed ? (
                  <>
                    <a
                      href="/"
                      style={{ display: "block", textAlign: "center", background: "#d15424", color: "#fff", fontWeight: 700, fontSize: 14, borderRadius: 12, padding: "13px 0", textDecoration: "none" }}
                    >
                      Try Again
                    </a>
                    <Link
                      to="/"
                      style={{ display: "block", textAlign: "center", color: "#64748b", fontSize: 13, fontWeight: 600, textDecoration: "none" }}
                    >
                      Go to homepage
                    </Link>
                  </>
                ) : (
                  <Link
                    to="/"
                    style={{ display: "block", textAlign: "center", background: "#0f172a", color: "#fff", fontWeight: 700, fontSize: 14, borderRadius: 12, padding: "13px 0", textDecoration: "none" }}
                  >
                    Go to Homepage
                  </Link>
                )}
              </div>

              {/* Help */}
              <p style={{ textAlign: "center", fontSize: 11, color: "#94a3b8", marginTop: 24 }}>
                Questions?{" "}
                <a href="mailto:hello@e1xacademy.net" style={{ color: "#d15424", textDecoration: "none", fontWeight: 600 }}>
                  hello@e1xacademy.net
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
